import React from 'react';
import { motion } from 'framer-motion';
import { UsersIcon, HomeIcon, CreditCardIcon, ArrowRightIcon, CheckIcon } from 'lucide-react';
import { useLanguage } from '../LanguageContext';
const EASE = [0.23, 1, 0.32, 1] as const;
const reveal = {
  initial: {
    opacity: 0,
    y: 20
  },
  whileInView: {
    opacity: 1,
    y: 0
  },
  viewport: {
    once: true,
    margin: '-80px'
  },
  transition: {
    duration: 0.6,
    ease: EASE
  }
};
type Bilingual = {
  en: string;
  fr: string;
};
const PATHWAYS: {
  id: string;
  icon: typeof UsersIcon;
  label: Bilingual;
  relationship: Bilingual;
  services: Bilingual[];
}[] = [{
  id: 'family',
  icon: UsersIcon,
  label: {
    en: 'Family',
    fr: 'Famille'
  },
  relationship: {
    en: 'Family memberships & registrations.',
    fr: 'Adhésions et inscriptions familiales.'
  },
  services: [{
    en: 'Recreation memberships',
    fr: 'Abonnements aux loisirs'
  }, {
    en: 'Program registrations',
    fr: 'Inscriptions aux activités'
  }]
}, {
  id: 'property',
  icon: HomeIcon,
  label: {
    en: 'Property',
    fr: 'Propriété'
  },
  relationship: {
    en: 'Permits · Water · Resident services.',
    fr: 'Permis · Eau · Services aux citoyens.'
  },
  services: [{
    en: 'Permits & licences',
    fr: 'Permis et licences'
  }, {
    en: 'Water services',
    fr: 'Services d’eau'
  }, {
    en: 'Resident services',
    fr: 'Services aux citoyens'
  }]
}, {
  id: 'membership',
  icon: CreditCardIcon,
  label: {
    en: 'Membership',
    fr: 'Adhésion'
  },
  relationship: {
    en: 'Access · Benefits · Pricing.',
    fr: 'Accès · Avantages · Tarification.'
  },
  services: [{
    en: 'Facility access',
    fr: 'Accès aux installations'
  }, {
    en: 'Member benefits',
    fr: 'Avantages pour les membres'
  }, {
    en: 'Preferred pricing',
    fr: 'Tarification préférentielle'
  }]
}];
const QR_PATTERN = [[1, 1, 1, 0, 1, 1, 1], [1, 0, 1, 1, 1, 0, 1], [1, 1, 1, 0, 1, 1, 1], [0, 0, 1, 1, 0, 1, 0], [1, 1, 0, 1, 1, 0, 1], [1, 0, 1, 0, 1, 1, 1], [1, 1, 1, 1, 0, 0, 1]];
function IdentityCard({
  isFR
}: {
  isFR: boolean;
}) {
  return <div className="w-full max-w-[19rem] rounded-2xl bg-white border border-[#ECEDF2] shadow-[0_2px_12px_rgba(18,18,18,0.05)] p-5" data-id="element-1543" data-mp="9siorba76bm">
      <div className="flex items-center gap-3" data-id="element-1544" data-mp="ezgmbz6xqh2i">
        <span className="relative shrink-0" data-id="element-1545" data-mp="a6dmxafh8dee">
          <span className="w-11 h-11 rounded-full bg-[#4F46E5]/10 flex items-center justify-center font-sans text-sm font-semibold text-[#4F46E5]" data-id="element-1546" data-mp="a4kfhsx1hp5c">
            MT
          </span>
          <span className="absolute -right-0.5 -bottom-0.5 w-4 h-4 rounded-full bg-[#4F46E5] flex items-center justify-center" data-id="element-1547" data-mp="iyfm75dt7nn3">
            <CheckIcon className="w-2.5 h-2.5 text-white" strokeWidth={3} aria-hidden="true" data-id="element-1548" data-mp="xtvmhs4zgfrx" />
          </span>
        </span>
        <span className="font-sans leading-tight" data-id="element-1549" data-mp="2znuwma6dcr31">
          <span className="block text-sm text-[#5B6478]" data-id="element-1550" data-mp="2up1e23c869w0">
            {isFR ? 'Bonjour,' : 'Hello,'}
          </span>
          <span className="block text-base font-semibold text-[#121212]" data-id="element-1551" data-mp="1a7ik93tqhlc5">
            Marie Tremblay
          </span>
        </span>
      </div>

      <div className="flex items-end justify-between gap-4 mt-6" data-id="element-1552" data-mp="1f7d971ty80uk">
        <div data-id="element-1553" data-mp="10futz68wyac9">
          <span className="flex items-end gap-0.5" aria-hidden="true" data-id="element-1554" data-mp="10ge5a44260hb">
            <span className="w-2 h-5 rounded-sm bg-[#4F46E5]" data-id="element-1555" data-mp="15epfify9md02" />
            <span className="w-2 h-3.5 rounded-sm bg-[#4F46E5]/60" data-id="element-1556" data-mp="rg7uwr3npgmd" />
          </span>
          <p className="font-sans text-[13px] font-semibold text-[#121212] mt-3" data-id="element-1557" data-mp="ric51qs5fbv3">
            {isFR ? 'Carte numérique BCITI' : 'BCITI Digital Card'}
          </p>
          <p className="font-sans text-[11px] text-[#5B6478] mt-1" data-id="element-1558" data-mp="hjcwe87dvg83">
            {isFR ? 'Vérifiée · Sécurisée · Chiffrée' : 'Verified · Secure · Encrypted'}
          </p>
        </div>
        <span className="grid grid-cols-7 gap-[2px] shrink-0" aria-hidden="true" data-id="element-1559" data-mp="2bwm7u5jhpbsz">
          {QR_PATTERN.flatMap((row, y) => row.map((cell, x) => <span key={`${x}-${y}`} className={`w-[5px] h-[5px] ${cell ? 'bg-[#121212]' : 'bg-transparent'}`} data-id="element-1560" data-mp="grvb2khluanu" data-mpi={x} />))}
        </span>
      </div>
    </div>;
}
function PathwayRow({
  pathway,
  isFR
}: {
  pathway: (typeof PATHWAYS)[number];
  isFR: boolean;
}) {
  const Icon = pathway.icon;
  const pick = (value: Bilingual) => isFR ? value.fr : value.en;
  return <div className="rounded-2xl bg-[#F7F8FA] px-5 py-5 lg:px-6 flex flex-col sm:flex-row sm:items-center gap-5" data-id="element-1561" data-mp="3p8gdfifqe5hz">
      <span className="w-14 h-14 shrink-0 rounded-full bg-white border border-[#ECEDF2] flex items-center justify-center" data-id="element-1562" data-mp="3k8vc51d39l23">
        <Icon className="w-6 h-6 text-[#121212]" strokeWidth={1.6} aria-hidden="true" data-id="element-1563" data-mp="3g8t8wnrpgjkp" />
      </span>

      <div className="sm:w-[13rem] shrink-0" data-id="element-1564" data-mp="3baro9yemsc74">
        <p className="font-sans text-[13px] font-semibold tracking-[0.1em] uppercase text-[#4F46E5]" data-id="element-1565" data-mp="369c6cf7r2lsy">
          {pick(pathway.label)}
        </p>
        <p className="font-sans text-sm text-[#5B6478] leading-snug mt-1.5" data-id="element-1566" data-mp="1ltnucl58h98y">
          {pick(pathway.relationship)}
        </p>
      </div>

      <ArrowRightIcon className="hidden sm:block w-5 h-5 text-[#121212]/30 shrink-0" strokeWidth={1.75} aria-hidden="true" data-id="element-1567" data-mp="1vp7op345fzvl" />

      <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5" data-id="element-1568" data-mp="1zp8d9gkyprgd">
        {pathway.services.map(service => <span key={service.en} className="rounded-xl bg-white border border-[#ECEDF2] px-4 py-3 font-sans text-sm text-[#121212] leading-snug" data-id="element-1569" data-mp="17zwts29hczpd">
            {pick(service)}
          </span>)}
      </div>
    </div>;
}
export function RelationshipPathways() {
  const {
    lang
  } = useLanguage();
  const isFR = lang === 'FR';
  return <div className="max-w-[1320px] mx-auto" data-id="element-1570" data-mp="k8rebo7gc9fl">
      {/* Header */}
      <motion.div {...reveal} className="text-center max-w-3xl mx-auto" data-id="element-1571" data-mp="oameuqhk6isb">
        <p className="font-sans text-xs font-semibold tracking-[0.24em] text-[#4F46E5] uppercase" data-id="element-1572" data-mp="1rmbbz8r63415">
          {isFR ? 'Des relations aux services concrets' : 'From relationships to real services'}
        </p>
        <h2 className="font-serif text-4xl md:text-5xl text-[#121212] leading-[1.12] tracking-tight mt-5" data-id="element-1573" data-mp="21c39obukcd9u">
          {isFR ? 'Chaque relation peut donner accès à un service.' : 'Every relationship can unlock a service.'}
        </h2>
        <p className="font-sans text-lg text-[#5B6478] mt-5" data-id="element-1574" data-mp="13pnvpk1chxr1">
          {isFR ? 'La bonne identité. La bonne relation. Le bon service.' : 'The right identity. The right relationship. The right service.'}
        </p>
      </motion.div>

      {/* Desktop / tablet diagram */}
      <motion.div {...reveal} transition={{
      duration: 0.6,
      delay: 0.08,
      ease: EASE
    }} className="hidden lg:grid grid-cols-[minmax(0,20rem)_5rem_minmax(0,1fr)] items-center mt-16" data-id="element-1575" data-mp="2t3j2woqmky91">
        <div className="flex flex-col items-center" data-id="element-1576" data-mp="2wkyhurfj4p6d">
          <IdentityCard isFR={isFR} data-id="element-1577" data-mp="2wock7zo70ydt" />
          <p className="font-sans text-[13px] font-semibold tracking-[0.14em] uppercase text-[#4F46E5] mt-7" data-id="element-1578" data-mp="31lcqaal1m14x">
            {isFR ? 'Une identité' : 'One identity'}
          </p>
          <p className="font-sans text-sm text-[#5B6478] text-center leading-snug mt-2 max-w-[17rem]" data-id="element-1579" data-mp="2rmn78eheuan3">
            {isFR ? 'Une personne vérifiée au centre de son écosystème.' : 'A verified person at the centre of their ecosystem.'}
          </p>
        </div>

        {/* Connectors */}
        <div className="self-stretch relative" aria-hidden="true" data-id="element-1580" data-mp="35ng4v830dluf">
          <svg viewBox="0 0 80 300" preserveAspectRatio="none" className="absolute inset-0 w-full h-full" data-id="element-1581" data-mp="35ktul0oqgs8i">
            {[52, 150, 248].map(y => <path key={y} d={`M0 150 C 40 150, 40 ${y}, 74 ${y}`} fill="none" stroke="#4F46E5" strokeOpacity="0.5" strokeWidth="1" strokeDasharray="4 4" vectorEffect="non-scaling-stroke" data-id="element-1582" data-mp="244sbqw6o4on" />)}
            {[52, 150, 248].map(y => <circle key={`dot-${y}`} cx="75" cy={y} r="3.5" fill="#4F46E5" data-id="element-1583" data-mp="1u5ec7karrbcg" />)}
          </svg>
        </div>

        <div className="space-y-4" data-id="element-1584" data-mp="2lyevb9i7ar36">
          {PATHWAYS.map(pathway => <PathwayRow key={pathway.id} pathway={pathway} isFR={isFR} data-id="element-1585" data-mp="2lwc78v0lz813" />)}
        </div>
      </motion.div>

      {/* Mobile / small tablet */}
      <div className="lg:hidden mt-12" data-id="element-1586" data-mp="2cwxiq14057j8">
        <div className="flex flex-col items-center" data-id="element-1587" data-mp="2d0369bwzg1cu">
          <IdentityCard isFR={isFR} data-id="element-1588" data-mp="27xubqb4gor8m" />
          <p className="font-sans text-[13px] font-semibold tracking-[0.14em] uppercase text-[#4F46E5] mt-6" data-id="element-1589" data-mp="281017rh05v4v">
            {isFR ? 'Une identité' : 'One identity'}
          </p>
          <p className="font-sans text-sm text-[#5B6478] text-center leading-snug mt-2 max-w-[19rem]" data-id="element-1590" data-mp="wiyuqiicedow">
            {isFR ? 'Une personne vérifiée au centre de son écosystème.' : 'A verified person at the centre of their ecosystem.'}
          </p>
        </div>

        <div className="relative mt-8 pl-6 space-y-5" data-id="element-1591" data-mp="wcy940z6slyt">
          <span className="absolute left-[3px] top-0 bottom-6 w-px bg-[#4F46E5]/35" aria-hidden="true" data-id="element-1592" data-mp="114pt1snu7tfz" />
          {PATHWAYS.map(pathway => <div key={pathway.id} className="relative" data-id="element-1593" data-mp="158e08a8k4n0p">
              <span className="absolute -left-6 top-8 w-[7px] h-[7px] rounded-full bg-[#4F46E5]" aria-hidden="true" data-id="element-1594" data-mp="1a4cwek04n9bo" />
              <PathwayRow pathway={pathway} isFR={isFR} data-id="element-1595" data-mp="1k3ag99mlyeoc" />
            </div>)}
        </div>
      </div>

      {/* Conclusion */}
      <motion.div {...reveal} transition={{
      duration: 0.6,
      delay: 0.12,
      ease: EASE
    }} className="text-center mt-16" data-id="element-1596" data-mp="3gwvpquoq2ibg">
        <p className="font-serif text-3xl md:text-4xl text-[#121212] leading-snug" data-id="element-1597" data-mp="3lwruiw58uds2">
          {isFR ? 'Une identité peut alimenter plusieurs services.' : 'One identity can power many services.'}
        </p>
        <p className="font-serif text-xl md:text-2xl text-[#5B6478] leading-snug mt-3" data-id="element-1598" data-mp="fhpuh3toec8t">
          {isFR ? 'Sans recréer la relation chaque fois.' : 'Without recreating the relationship each time.'}
        </p>
        <div className="w-14 h-[2px] bg-[#4F46E5] mx-auto mt-7" aria-hidden="true" data-id="element-1599" data-mp="pfngdko2bwy9" />
      </motion.div>
    </div>;
}